<template>
  <div v-if="isOpen" class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3>Upload SCORM Package</h3>
        <button class="close-button" @click="closeModal">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <div
          v-if="!selectedFile"
          class="upload-area"
          @drop="handleDrop"
          @dragover.prevent
          @dragenter.prevent
        >
          <div class="upload-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
              ></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <h4>Upload SCORM Package</h4>
          <p>Drag and drop your SCORM file here, or click to browse</p>
          <p class="file-info">Supported formats: .zip</p>

          <label class="upload-button">
            Browse Files
            <input
              ref="fileInput"
              type="file"
              @change="handleFileSelect"
              accept=".zip"
              style="display: none"
            />
          </label>
        </div>

        <div v-else class="file-preview">
          <div class="file-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
              ></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
          </div>
          <div class="file-details">
            <h4>{{ selectedFile.name }}</h4>
            <p>{{ formatFileSize(selectedFile.size) }}</p>
          </div>
          <button class="remove-file-button" @click="removeFile">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="m19 6-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"></path>
              <path d="m8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </button>
        </div>

        <div v-if="errorMessage" class="error-message">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          {{ errorMessage }}
        </div>
      </div>

      <div class="modal-footer">
        <button
          class="cancel-button"
          @click="closeModal"
          :disabled="isUploading"
        >
          Cancel
        </button>
        <button
          class="upload-confirm-button"
          @click="confirmUpload"
          :disabled="!selectedFile || isUploading"
        >
          <div v-if="isUploading" class="spinner"></div>
          <span v-else>Upload SCORM</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineEmits, defineProps, defineExpose } from "vue";

defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close", "upload"]);

const selectedFile = ref(null);
const errorMessage = ref("");
const isUploading = ref(false);
const fileInput = ref(null);

const closeModal = () => {
  if (!isUploading.value) {
    selectedFile.value = null;
    errorMessage.value = "";
    emit("close");
  }
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    validateAndSetFile(file);
  }
};

const handleDrop = (event) => {
  event.preventDefault();
  const file = event.dataTransfer.files[0];
  if (file) {
    validateAndSetFile(file);
  }
};

const validateAndSetFile = (file) => {
  errorMessage.value = "";

  // Check file type
  if (!file.name.toLowerCase().endsWith(".zip")) {
    errorMessage.value =
      "Please select a ZIP file containing your SCORM package.";
    return;
  }

  // Check file size (limit to 100MB)
  if (file.size > 100 * 1024 * 1024) {
    errorMessage.value = "File size should be less than 100MB.";
    return;
  }

  selectedFile.value = file;
};

const removeFile = () => {
  selectedFile.value = null;
  errorMessage.value = "";
  if (fileInput.value) {
    fileInput.value.value = "";
  }
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const confirmUpload = () => {
  if (selectedFile.value && !isUploading.value) {
    isUploading.value = true;
    emit("upload", selectedFile.value);
  }
};

// Reset uploading state when modal closes
const resetUploadingState = () => {
  isUploading.value = false;
};

defineExpose({
  resetUploadingState,
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
}

.modal-header h3 {
  margin: 0;
  color: var(--primary-blue);
  font-size: 1.25rem;
  font-weight: 600;
}

.close-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.2s ease;
}

.close-button:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 0 24px;
  flex: 1;
  overflow-y: auto;
}

.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;
  background: #fafafa;
}

.upload-area:hover {
  border-color: var(--primary-blue);
  background-color: rgba(0, 61, 106, 0.02);
}

.upload-icon {
  color: #6b7280;
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
}

.upload-area h4 {
  margin: 0 0 8px 0;
  color: #374151;
  font-size: 1.1rem;
  font-weight: 600;
}

.upload-area p {
  margin: 0 0 4px 0;
  color: #6b7280;
  font-size: 0.95rem;
}

.file-info {
  font-size: 0.85rem !important;
  color: #9ca3af !important;
}

.upload-button {
  display: inline-block;
  background: linear-gradient(135deg, #003d6a 0%, #00497e 100%);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  margin-top: 20px;
  font-size: 0.95rem;
}

.upload-button:hover {
  background: linear-gradient(135deg, #00497e 0%, #005a94 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 61, 106, 0.3);
}

.file-preview {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: rgba(0, 61, 106, 0.05);
  border: 1px solid rgba(0, 61, 106, 0.1);
  border-radius: 12px;
  margin-bottom: 16px;
}

.file-icon {
  color: var(--primary-blue);
  flex-shrink: 0;
}

.file-details {
  flex: 1;
}

.file-details h4 {
  margin: 0 0 4px 0;
  color: #374151;
  font-size: 1rem;
  font-weight: 500;
}

.file-details p {
  margin: 0;
  color: #6b7280;
  font-size: 0.9rem;
}

.remove-file-button {
  background: rgba(239, 68, 68, 0.1);
  border: none;
  color: #ef4444;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.remove-file-button:hover {
  background: rgba(239, 68, 68, 0.2);
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  color: #dc2626;
  font-size: 0.9rem;
  margin-top: 16px;
}

.modal-footer {
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.cancel-button {
  padding: 10px 20px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.cancel-button:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.cancel-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.upload-confirm-button {
  padding: 10px 20px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
  justify-content: center;
}

.upload-confirm-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.upload-confirm-button:disabled {
  background: #d1d5db;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .modal-container {
    margin: 0;
    border-radius: 0;
    height: 100vh;
    max-height: none;
  }

  .upload-area {
    padding: 32px 16px;
  }

  .modal-footer {
    flex-direction: column;
  }

  .cancel-button,
  .upload-confirm-button {
    width: 100%;
  }
}
</style>
